import * as React from 'react';

const DEFAULT_MIN_SIZE = 80;
const STORAGE_PREFIX = 'clizod:columnWidths:';

export type FitColumn = {
    id: string;
    /** Initial width, treated as a proportion of the available width. */
    size: number;
    minSize?: number;
};

type UseFitColumnSizingOptions = {
    columns: FitColumn[];
    /** Horizontal padding inside each row that columns must not occupy. */
    horizontalPadding?: number;
    /** For retrieving user-resized columns widths from sessionStorage. */
    storageKey?: string;
};

type ResizeStartEvent = React.MouseEvent | React.TouchEvent;

const getClientX = (event: ResizeStartEvent | MouseEvent | TouchEvent) =>
    'touches' in event ? (event.touches[0]?.clientX ?? 0) : event.clientX;

const cssVarName = (id: string) => `--col-${id}`;

/** CSS width for a column — reads the variable the hook writes on the container. */
const getColumnWidth = (id: string) => `var(${cssVarName(id)})`;

const sumOf = (widths: Record<string, number>, ids: string[]) =>
    ids.reduce((sum, id) => sum + (widths[id] ?? 0), 0);

const scaleTo =(widths: Record<string, number>, ids: string[], target: number) => {
    const total = sumOf(widths, ids);
    if (total <= 0 || target <= 0) return widths;
    return Object.fromEntries(ids.map(id => [id, ((widths[id] ?? 0) / total) * target]));
};

// Storage access can throw (private mode, blocked site data), so every read
// and write falls back to not persisting.

/** Saved widths for these columns, or null if missing, unreadable or saved for different columns. */
const readStoredWidths = (key: string | undefined, ids: string[]) => {
    if (!key) return null;
    try {
        const raw = sessionStorage.getItem(STORAGE_PREFIX + key);
        if (!raw) return null;
        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed !== 'object' || parsed === null) return null;
        const stored = parsed as Record<string, unknown>;
        const isValid = ids.every(id => {
            const value = stored[id];
            return typeof value === 'number' && value > 0;
        });
        return isValid ? Object.fromEntries(ids.map(id => [id, stored[id] as number])) : null;
    } catch {
        return null;
    }
};

/** Saves widths as proportions of their total, so they still fit at a different window width. */
const writeStoredWidths = (key: string | undefined, ids: string[], widths: Record<string, number>) => {
    if (!key) return;
    try {
        sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(scaleTo(widths, ids, 1)));
    } catch {
        // Widths just won't be remembered.
    }
};

const clearStoredWidths = (key: string | undefined) => {
    if (!key) return;
    try {
        sessionStorage.removeItem(STORAGE_PREFIX + key);
    } catch {
        // Nothing to clear.
    }
};

/**
 * Keeps column widths summing to exactly the container's width, so the grid
 * never scrolls horizontally. Dragging a column's edge only trades width with
 * its right-hand neighbour (the last column has no resize handle), and widths
 * are rescaled proportionally whenever the container resizes. With a
 * storageKey, resized widths are remembered for the browser tab's session.
 */
export const useFitColumnSizing = ({ columns, horizontalPadding = 0, storageKey }: UseFitColumnSizingOptions) => {
    const ids = React.useMemo(() => columns.map(column => column.id), [columns]);
    const defaults = React.useMemo(
        () => Object.fromEntries(columns.map(column => [column.id, column.size])),
        [columns]
    );

    const [container, setContainer] = React.useState<HTMLElement | null>(null);
    const [availableWidth, setAvailableWidth] = React.useState(0);
    // Stored proportions are scaled to the defaults' total so they're sensible
    // pixel values even before the container has been measured.
    const [widths, setWidths] = React.useState<Record<string, number>>(() => {
        const stored = readStoredWidths(storageKey, ids);
        return stored ? scaleTo(stored, ids, sumOf(defaults, ids)) : defaults;
    });

    // Measure synchronously as soon as the container attaches, so the first
    // paint already has fitted widths (the ResizeObserver below only reports
    // after a frame, which showed as the columns snapping into place).
    React.useLayoutEffect(() => {
        if (!container) return;
        const available = container.clientWidth - horizontalPadding;
        setAvailableWidth(available);
        setWidths(previous => scaleTo(previous, ids, available));
    }, [container, horizontalPadding, ids]);

    React.useEffect(() => {
        if (!container) return;
        const observer = new ResizeObserver(([entry]) => {
            const available = entry.contentRect.width - horizontalPadding;
            setAvailableWidth(available);
            setWidths(previous => scaleTo(previous, ids, available));
        });
        observer.observe(container);
        return () => observer.disconnect();
    }, [container, horizontalPadding, ids]);

    // Layout effect so the variables are in place before the first paint.
    React.useLayoutEffect(() => {
        if (!container) return;
        for (const id of ids) {
            container.style.setProperty(cssVarName(id), `${widths[id]}px`);
        }
    }, [container, ids, widths]);

    // Only user resizes are saved — not rescales from the container resizing.
    const shouldSaveRef = React.useRef(false);
    React.useEffect(() => {
        if (!shouldSaveRef.current) return;
        shouldSaveRef.current = false;
        writeStoredWidths(storageKey, ids, widths);
    }, [storageKey, ids, widths]);

    // Tear down window listeners if unmounted mid-drag.
    const endDragRef = React.useRef<(() => void) | null>(null);
    React.useEffect(() => () => endDragRef.current?.(), []);

    const getResizeHandler = (id: string) => {
        const index = ids.indexOf(id);
        const nextId = ids[index + 1];
        if (index < 0 || nextId === undefined) return undefined;

        return (event: ResizeStartEvent) => {
            if (!container) return;
            const startX = getClientX(event);
            const pairTotal = widths[id] + widths[nextId];
            const startLeft = widths[id];
            const minLeft = columns[index].minSize ?? DEFAULT_MIN_SIZE;
            const minRight = columns[index + 1].minSize ?? DEFAULT_MIN_SIZE;
            if (pairTotal < minLeft + minRight) return;

            let left = startLeft;
            let frame = 0;

            const onMove = (moveEvent: MouseEvent | TouchEvent) => {
                left = Math.min(Math.max(startLeft + getClientX(moveEvent) - startX, minLeft), pairTotal - minRight);
                if (frame) return;
                frame = requestAnimationFrame(() => {
                    frame = 0;
                    container.style.setProperty(cssVarName(id), `${left}px`);
                    container.style.setProperty(cssVarName(nextId), `${pairTotal - left}px`);
                });
            };

            const previousUserSelect = document.body.style.userSelect;
            const endDrag = () => {
                cancelAnimationFrame(frame);
                shouldSaveRef.current = true;
                setWidths(previous => ({ ...previous, [id]: left, [nextId]: pairTotal - left }));
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('touchmove', onMove);
                window.removeEventListener('mouseup', endDrag);
                window.removeEventListener('touchend', endDrag);
                window.removeEventListener('touchcancel', endDrag);
                document.body.style.userSelect = previousUserSelect;
                endDragRef.current = null;
            };

            endDragRef.current?.();
            endDragRef.current = endDrag;
            document.body.style.userSelect = 'none';
            window.addEventListener('mousemove', onMove);
            window.addEventListener('touchmove', onMove);
            window.addEventListener('mouseup', endDrag);
            window.addEventListener('touchend', endDrag);
            window.addEventListener('touchcancel', endDrag);
        };
    };

    const resetSizes = () => {
        clearStoredWidths(storageKey);
        setWidths(scaleTo(defaults, ids, availableWidth));
    };

    return { containerRef: setContainer, widths, getColumnWidth, getResizeHandler, resetSizes };
};
